if (!customElements.get('product-upsells')) {
  class ProductUpsells extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.setupInputs();
        return;
      }

      this.initialized = true;
      this.productUpsellsId = this.dataset.productUpsells;
      this.sectionId = this.dataset.sectionId;
      this.blockId = this.dataset.blockId;
      this.parentProductId = this.dataset.parentProductId || null;
      this.parentVariantId = this.dataset.parentVariantId || null;
      this.analyticsViewEvent = this.dataset.analyticsViewEvent || null;
      this.analyticsAddEvent = this.dataset.analyticsAddEvent || null;
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.isProcessing = false;
      this.pendingQueue = [];
      this.viewTracked = false;

      this.boundOnInputChange = this.onInputChange.bind(this);
      this.onCartUpdateHandler = this.onCartUpdate.bind(this);
      this.onCartErrorHandler = this.onCartError.bind(this);
      this.onVariantChangeHandler = this.onVariantChange.bind(this);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdateHandler);
        this.cartErrorUnsubscribe = subscribe(PUB_SUB_EVENTS.cartError, this.onCartErrorHandler);
        this.variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChangeHandler);
      }

      this.setupInputs();
      this.queueViewTracking();
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscribe?.();
      this.cartErrorUnsubscribe?.();
      this.variantChangeUnsubscribe?.();
      this.intersectionObserver?.disconnect();
    }

    setupInputs() {
      this.inputs = Array.from(
        this.querySelectorAll('input.product-upsells__checkbox[data-variant-id]')
      );
      this.inputs.forEach((input) => {
        input.removeEventListener('change', this.boundOnInputChange);
        input.addEventListener('change', this.boundOnInputChange);
        this.syncInputState(input);
      });
    }

    syncInputState(input) {
      const item = input.closest('.product-upsells__item');
      if (!item) return;
      const isSelected = input.checked && !input.disabled;
      item.classList.remove('product-upsells__item--selected');

      if (isSelected) {
        item.classList.add('product-upsells__item--selected');
      }

      const action = item.querySelector('.product-upsells__action');
      if (action) {
        if (action.tagName === 'LABEL') {
          action.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          action.classList.toggle('product-upsells__action--disabled', input.disabled);
        } else {
          action.removeAttribute('aria-pressed');
        }
        action.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');
      }
    }

    onInputChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      this.syncInputState(input);
    }

    onCartUpdate(payload) {
      if (!payload || payload.source !== 'product-form') return;

      const variantId = payload.productVariantId ? String(payload.productVariantId) : '';
      if (!variantId || (this.parentVariantId && variantId !== this.parentVariantId)) {
        return;
      }

      if (!this.inputs || !this.inputs.length) return;
      const selectedInputs = this.inputs.filter((input) => input.checked && !input.disabled);
      if (!selectedInputs.length) return;

      if (this.isProcessing) {
        // Queue additional selections to run after the current batch.
        this.pendingQueue.push(
          ...selectedInputs.map((input) => this.mapInputToItem(input))
        );
        return;
      }

      this.pendingQueue = selectedInputs.map((input) => this.mapInputToItem(input));
      this.startProcessing();
    }

    mapInputToItem(input) {
      return {
        variantId: input.dataset.variantId,
        quantity: parseInt(input.dataset.quantity || '1', 10) || 1,
        productId: input.dataset.productId || null,
        productHandle: input.dataset.productHandle || null,
        productTitle: input.dataset.productTitle || '',
        variantTitle: input.dataset.variantTitle || '',
        price: parseInt(input.dataset.price || '0', 10) || 0,
        compareAtPrice: parseInt(input.dataset.compareAtPrice || '0', 10) || 0,
      };
    }

    startProcessing() {
      if (!this.pendingQueue.length) return;
      this.isProcessing = true;
      this.classList.add('product-upsells--processing');
      this.toggleInputsDisabled(true);
      this.processNext();
    }

    processNext() {
      if (!this.pendingQueue.length) {
        this.finishProcessing({ clearSelection: true });
        return;
      }

      const nextItem = this.pendingQueue.shift();
      this.addVariantToCart(nextItem)
        .then(() => {
          this.emitAddAnalytics(nextItem);
          this.processNext();
        })
        .catch((error) => {
          console.error('[product-upsells] Failed to add upsell', error);
          this.finishProcessing({ clearSelection: false });
        });
    }

    addVariantToCart(item) {
      const config = fetchConfig?.('javascript') || { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', item.variantId);
      formData.append('quantity', item.quantity);
      formData.append('properties[_upsell_parent_product_id]', this.parentProductId || '');
      formData.append('properties[_upsell_parent_variant_id]', this.parentVariantId || '');
      formData.append('properties[_upsell_block_id]', this.blockId || '');

      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        const sections = this.cart.getSectionsToRender();
        if (Array.isArray(sections) && sections.length) {
          formData.append(
            'sections',
            sections.map((section) => section.id)
          );
        }
        formData.append('sections_url', window.location.pathname);
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      config.body = formData;

      return fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish?.(PUB_SUB_EVENTS.cartError, {
              source: 'product-upsells',
              productVariantId: item.variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            throw new Error(response.description || 'Upsell add failed');
          }

          if (this.cart && typeof this.cart.renderContents === 'function') {
            this.cart.renderContents(response);
            this.cart.classList?.remove?.('is-empty');
          }

          publish?.(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-upsells',
            productVariantId: item.variantId,
            cartData: { items: [response] },
            context: {
              parentVariantId: this.parentVariantId,
              parentProductId: this.parentProductId,
            },
          });
        });
    }

    onCartError(payload) {
      if (!payload || payload.source !== 'product-form') return;
      this.finishProcessing({ clearSelection: false });
    }

    finishProcessing({ clearSelection }) {
      this.isProcessing = false;
      this.classList.remove('product-upsells--processing');
      this.pendingQueue = [];
      this.toggleInputsDisabled(false, { clearSelection });
    }

    toggleInputsDisabled(disabled, { clearSelection = false } = {}) {
      this.inputs?.forEach((input) => {
        const isAvailable = input.dataset.available !== 'false';
        input.disabled = disabled || !isAvailable;
        if (clearSelection) {
          input.checked = false;
        }
        this.syncInputState(input);
      });
    }

    onVariantChange(payload) {
      const variant = payload?.data?.variant;
      const html = payload?.data?.html;

      if (variant?.id) {
        this.parentVariantId = String(variant.id);
        this.dataset.parentVariantId = this.parentVariantId;
      }

      if (!html || !this.productUpsellsId) return;

      const nextRoot = html.querySelector(
        `[data-product-upsells="${this.productUpsellsId}"]`
      );

      if (!nextRoot) {
        this.innerHTML = '';
        this.dataset.hasUpsells = 'false';
        this.setAttribute('hidden', 'hidden');
        this.finishProcessing({ clearSelection: true });
        return;
      }

      const currentContainer = this.querySelector('[data-upsells-container]');
      const nextContainer = nextRoot.querySelector('[data-upsells-container]');

      Array.from(nextRoot.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-')) {
          this.setAttribute(attr.name, attr.value);
        }
      });

      if (nextRoot.hasAttribute('hidden')) {
        this.setAttribute('hidden', 'hidden');
      } else {
        this.removeAttribute('hidden');
      }

      if (currentContainer && nextContainer) {
        currentContainer.innerHTML = nextContainer.innerHTML;
      } else {
        this.innerHTML = nextRoot.innerHTML;
      }

      this.analyticsViewEvent = this.dataset.analyticsViewEvent || this.analyticsViewEvent;
      this.analyticsAddEvent = this.dataset.analyticsAddEvent || this.analyticsAddEvent;
      this.parentProductId = this.dataset.parentProductId || this.parentProductId;
      this.parentVariantId = this.dataset.parentVariantId || this.parentVariantId;

      this.setupInputs();
      this.finishProcessing({ clearSelection: true });
      this.queueViewTracking(true);
    }

    queueViewTracking(force = false) {
      if (this.dataset.hasUpsells !== 'true') {
        this.viewTracked = false;
        this.intersectionObserver?.disconnect();
        return;
      }

      if (this.viewTracked && !force) return;

      if (!('IntersectionObserver' in window)) {
        this.trackView();
        return;
      }

      this.intersectionObserver?.disconnect();
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.trackView();
            this.intersectionObserver?.disconnect();
          }
        });
      }, { rootMargin: '0px 0px -20%' });

      this.intersectionObserver.observe(this);
    }

    trackView() {
      if (this.viewTracked || this.dataset.hasUpsells !== 'true') return;
      this.viewTracked = true;

      if (this.analyticsViewEvent) {
        if (window.Shopify?.analytics?.publish) {
          window.Shopify?.analytics?.publish?.(this.analyticsViewEvent, {
            parent_product_id: this.parentProductId,
            parent_variant_id: this.parentVariantId,
            upsell_block_id: this.blockId,
          });
        } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({
            event: this.analyticsViewEvent,
            parent_product_id: this.parentProductId,
            parent_variant_id: this.parentVariantId,
            upsell_block_id: this.blockId,
          });
        }
      }
    }

    emitAddAnalytics(item) {
      if (!this.analyticsAddEvent) return;

      const payload = {
        parent_product_id: this.parentProductId,
        parent_variant_id: this.parentVariantId,
        upsell_block_id: this.blockId,
        upsell_product_id: item.productId,
        upsell_product_handle: item.productHandle,
        upsell_variant_id: item.variantId,
        price: item.price,
        compare_at_price: item.compareAtPrice,
      };

      if (window.Shopify?.analytics?.publish) {
        window.Shopify?.analytics?.publish?.(this.analyticsAddEvent, payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: this.analyticsAddEvent, ...payload });
      }
    }
  }

  customElements.define('product-upsells', ProductUpsells);
}
